/**
 * Schedule page — Calendar view of battalion duty schedule.
 * Sprint 8, SDD §7.2, FR-SCHED-11
 *
 * Features:
 * - Month calendar grid with battalion assignments, color-coded by battalion
 * - Battalions shown as pill in top-right of each calendar day
 * - Holidays: white background, battalion-colored date number
 * - Vertical infinite scrolling across months (auto-loads on scroll)
 * - "Today" button to jump back to current date
 * - Outlook-inspired UI elements
 * - Open shift / staffing hole indicators with color coding
 * - Member's own battalion visually highlighted
 *
 * Battalion Colors (matching printed 2026 Duty Calendar):
 *   Bn 1 = Cyan, Bn 2 = Lime Green, Bn 3 = Red, Bn 4 = Orange, Bn 5 = Indigo
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useScheduleMonth } from '../hooks/useSchedule';
import type { ScheduleDayEntry } from '../hooks/useSchedule';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { computeBattalionForDate } from '../lib/schedule';
import AppLayout from '../components/layout/AppLayout';
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon } from 'lucide-react';

// ── Battalion Colors ─────────────────────────────────────────────────────
// Bn 1 = Cyan, Bn 2 = Lime Green, Bn 3 = Red, Bn 4 = Orange, Bn 5 = Indigo
// Pills use semitransparent bg + colored text + colored border

const BATTALION_COLORS: Record<
  number,
  { bg: string; text: string; border: string; pillBg: string; pillText: string; pillBorder: string }
> = {
  1: {
    bg: 'rgba(0, 188, 212, 0.10)',
    text: '#4DD0E1',
    border: 'rgba(0, 188, 212, 0.25)',
    pillBg: 'rgba(0, 188, 212, 0.15)',
    pillText: '#4DD0E1',
    pillBorder: 'rgba(0, 188, 212, 0.4)',
  }, // Cyan
  2: {
    bg: 'rgba(76, 175, 80, 0.10)',
    text: '#66BB6A',
    border: 'rgba(76, 175, 80, 0.25)',
    pillBg: 'rgba(76, 175, 80, 0.15)',
    pillText: '#66BB6A',
    pillBorder: 'rgba(76, 175, 80, 0.4)',
  }, // Lime Green
  3: {
    bg: 'rgba(229, 57, 53, 0.10)',
    text: '#EF5350',
    border: 'rgba(229, 57, 53, 0.25)',
    pillBg: 'rgba(229, 57, 53, 0.15)',
    pillText: '#EF5350',
    pillBorder: 'rgba(229, 57, 53, 0.4)',
  }, // Red
  4: {
    bg: 'rgba(245, 124, 0, 0.10)',
    text: '#FFA726',
    border: 'rgba(245, 124, 0, 0.25)',
    pillBg: 'rgba(245, 124, 0, 0.15)',
    pillText: '#FFA726',
    pillBorder: 'rgba(245, 124, 0, 0.4)',
  }, // Orange
  5: {
    bg: 'rgba(63, 81, 181, 0.10)',
    text: '#7986CB',
    border: 'rgba(63, 81, 181, 0.25)',
    pillBg: 'rgba(63, 81, 181, 0.15)',
    pillText: '#7986CB',
    pillBorder: 'rgba(63, 81, 181, 0.4)',
  }, // Indigo
};

function getBattalionStyle(battalionNumber: number) {
  return (
    BATTALION_COLORS[battalionNumber] ?? {
      bg: '#111111',
      text: '#999999',
      border: '#1a1a1a',
      pillBg: 'rgba(100,100,100,0.15)',
      pillText: '#999',
      pillBorder: 'rgba(100,100,100,0.3)',
    }
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────

const DAY_NAMES_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function isToday(dateStr: string): boolean {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return dateStr === todayStr;
}

function getTodayYM(): { year: number; month: number } {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() + 1 };
}

function addMonthsToYM(year: number, month: number, delta: number): { year: number; month: number } {
  const m0 = month - 1 + delta;
  const y = year + Math.floor(m0 / 12);
  const m = (((m0 % 12) + 12) % 12) + 1;
  return { year: y, month: m };
}

/**
 * For Sundays (which the backend doesn't return data for since they're
 * continuations of Saturday weekend shifts), find the Saturday entry
 * from the day map and return it so we can show the battalion.
 */
function getSundayBattalionEntry(
  year: number,
  month: number,
  day: number,
  dayMap: Map<string, ScheduleDayEntry>,
): ScheduleDayEntry | null {
  // Look up the previous day (Saturday)
  const satDate = new Date(Date.UTC(year, month - 1, day));
  satDate.setUTCDate(satDate.getUTCDate() - 1);
  const satStr = `${satDate.getUTCFullYear()}-${String(satDate.getUTCMonth() + 1).padStart(2, '0')}-${String(satDate.getUTCDate()).padStart(2, '0')}`;
  return dayMap.get(satStr) ?? null;
}

// ── Component ────────────────────────────────────────────────────────────

export default function Schedule() {
  const { user } = useAuth();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const memberBattalionId = user?.battalion?.id ?? null;

  return (
    <AppLayout title="Duty Schedule">
      <div className="w-full">
        {/* Battalion legend */}
        <div className="mt-4 mb-4">
          <BattalionLegend memberBattalionId={memberBattalionId} isDesktop={isDesktop} />
        </div>

        {/* Calendar */}
        <ScrollableCalendar memberBattalionId={memberBattalionId} isDesktop={isDesktop} />
      </div>
    </AppLayout>
  );
}

// ── Battalion Legend ──────────────────────────────────────────────────────

function BattalionLegend({ memberBattalionId, isDesktop }: { memberBattalionId: number | null; isDesktop: boolean }) {
  return (
    <div className="flex gap-2 flex-wrap items-center bg-surface-raised rounded-xl border border-edge-muted px-4 py-3">
      <span className="text-[11px] font-semibold text-content-tertiary uppercase tracking-wider mr-1">Battalions</span>
      {[1, 2, 3, 4, 5].map((bn) => {
        const s = getBattalionStyle(bn);
        const isOwn = memberBattalionId === bn;
        return (
          <div key={bn} className="flex items-center gap-1">
            <span
              className="px-2.5 py-1 rounded-md text-[11px] font-bold leading-none whitespace-nowrap"
              style={{
                backgroundColor: s.pillBg,
                color: s.pillText,
                border: `1px solid ${s.pillBorder}`,
                boxShadow: isOwn ? `0 0 0 2px ${s.text}, 0 0 8px ${s.pillBg}` : undefined,
              }}
            >
              {isDesktop ? `Battalion ${bn}` : `B${bn}`}
            </span>
            {isOwn && <span className="text-[10px] text-action-primary font-bold">(You)</span>}
          </div>
        );
      })}
    </div>
  );
}

// ── Scrollable Calendar (vertical month scrolling with infinite scroll) ──

function ScrollableCalendar({
  memberBattalionId,
  isDesktop,
}: {
  memberBattalionId: number | null;
  isDesktop: boolean;
}) {
  const today = getTodayYM();

  // Initialize with 2 months before, current, and 2 months after
  const [months, setMonths] = useState<{ year: number; month: number }[]>(() => {
    const result: { year: number; month: number }[] = [];
    for (let i = -2; i <= 2; i++) {
      result.push(addMonthsToYM(today.year, today.month, i));
    }
    return result;
  });

  const scrollRef = useRef<HTMLDivElement>(null);
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef(false);

  const goToToday = useCallback(() => {
    const t = getTodayYM();
    const result: { year: number; month: number }[] = [];
    for (let i = -2; i <= 2; i++) {
      result.push(addMonthsToYM(t.year, t.month, i));
    }
    setMonths(result);
    // Scroll to the middle (current month) after render
    setTimeout(() => {
      const el = scrollRef.current;
      if (!el) return;
      const panels = el.querySelectorAll('[data-month-panel]');
      if (panels.length >= 3) {
        (panels[2] as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const addMonthAbove = useCallback(() => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setMonths((prev) => {
      const first = prev[0];
      const newMonth = addMonthsToYM(first.year, first.month, -1);
      return [newMonth, ...prev];
    });
    // Preserve scroll position when adding above
    const el = scrollRef.current;
    if (el) {
      const prevScrollHeight = el.scrollHeight;
      requestAnimationFrame(() => {
        el.scrollTop += el.scrollHeight - prevScrollHeight;
        isLoadingRef.current = false;
      });
    } else {
      isLoadingRef.current = false;
    }
  }, []);

  const addMonthBelow = useCallback(() => {
    if (isLoadingRef.current) return;
    isLoadingRef.current = true;
    setMonths((prev) => {
      const last = prev[prev.length - 1];
      const newMonth = addMonthsToYM(last.year, last.month, 1);
      return [...prev, newMonth];
    });
    requestAnimationFrame(() => {
      isLoadingRef.current = false;
    });
  }, []);

  // Infinite scroll with IntersectionObserver
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const topObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          addMonthAbove();
        }
      },
      { root: scrollEl, rootMargin: '200px 0px 0px 0px', threshold: 0 },
    );

    const bottomObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          addMonthBelow();
        }
      },
      { root: scrollEl, rootMargin: '0px 0px 200px 0px', threshold: 0 },
    );

    const topEl = topSentinelRef.current;
    const bottomEl = bottomSentinelRef.current;

    if (topEl) topObserver.observe(topEl);
    if (bottomEl) bottomObserver.observe(bottomEl);

    return () => {
      topObserver.disconnect();
      bottomObserver.disconnect();
    };
  }, [addMonthAbove, addMonthBelow]);

  // On mount, scroll to current month (index 2 in the initial 5-month list)
  useEffect(() => {
    const scrollToToday = () => {
      const el = scrollRef.current;
      if (!el) return;
      // Try to find today's specific cell first
      const todayCell = el.querySelector('[data-today="true"]');
      if (todayCell) {
        (todayCell as HTMLElement).scrollIntoView({ block: 'center' });
        return;
      }
      // Fallback to current month panel
      const panels = el.querySelectorAll('[data-month-panel]');
      if (panels.length >= 3) {
        (panels[2] as HTMLElement).scrollIntoView({ block: 'start' });
      }
    };
    // Try multiple times to ensure content has rendered
    setTimeout(scrollToToday, 200);
    setTimeout(scrollToToday, 500);
  }, []);

  const isCurrentMonthOnly = months.length === 5 && months[2].year === today.year && months[2].month === today.month;

  return (
    <div>
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-3 bg-surface-raised rounded-xl border border-edge-muted px-4 py-2.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={goToToday}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors duration-150 cursor-pointer
              ${
                isCurrentMonthOnly
                  ? 'bg-action-primary text-content-on-primary border-transparent'
                  : 'bg-surface-base text-content-primary border-edge-default hover:bg-surface-sunken'
              }`}
          >
            <CalendarIcon className="h-3.5 w-3.5" />
            Today
          </button>
        </div>

        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={addMonthAbove}
            className="p-1.5 rounded-lg text-content-secondary hover:bg-surface-sunken cursor-pointer border-none bg-transparent transition-colors duration-150"
            aria-label="Earlier month"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-semibold text-content-primary min-w-[140px] text-center">
            {`${MONTH_NAMES[today.month - 1]} ${today.year}`}
          </span>
          <button
            type="button"
            onClick={addMonthBelow}
            className="p-1.5 rounded-lg text-content-secondary hover:bg-surface-sunken cursor-pointer border-none bg-transparent transition-colors duration-150"
            aria-label="Later month"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Year mini-nav */}
        <div className="text-xs text-content-tertiary hidden sm:block">{months[0].year}</div>
      </div>

      {/* Scrollable month panels */}
      <div
        ref={scrollRef}
        className="overflow-y-auto"
        style={{ maxHeight: isDesktop ? 'calc(100vh - 220px)' : 'calc(100dvh - 260px)' }}
      >
        {/* Top sentinel for infinite scroll */}
        <div ref={topSentinelRef} className="h-1" />

        {months.map(({ year, month }) => (
          <MonthPanel
            key={`${year}-${month}`}
            year={year}
            month={month}
            memberBattalionId={memberBattalionId}
            isDesktop={isDesktop}
          />
        ))}

        {/* Bottom sentinel for infinite scroll */}
        <div ref={bottomSentinelRef} className="h-1" />
      </div>
    </div>
  );
}

// ── Month Panel ───────────────────────────────────────────────────────────

function MonthPanel({
  year,
  month,
  memberBattalionId,
  isDesktop,
}: {
  year: number;
  month: number;
  memberBattalionId: number | null;
  isDesktop: boolean;
}) {
  const schedule = useScheduleMonth(year, month);

  // Build the calendar grid
  const firstDayOfMonth = new Date(Date.UTC(year, month - 1, 1));
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const startDow = firstDayOfMonth.getUTCDay(); // 0=Sun, already first column

  const dayMap = new Map<string, ScheduleDayEntry>();
  for (const entry of schedule.days) {
    dayMap.set(entry.date, entry);
  }

  const cells: (number | null)[] = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const cellHeight = isDesktop ? 'min-h-[84px]' : 'min-h-[60px]';

  return (
    <div className="mb-6" data-month-panel>
      {/* Month header */}
      <div className="flex items-center gap-2 mb-2 px-1">
        <h2 className="text-base font-bold text-content-primary">
          {MONTH_NAMES[month - 1]} {year}
        </h2>
      </div>

      {schedule.loading && schedule.days.length === 0 ? (
        <div className="text-center p-10 text-content-tertiary text-xs">Loading schedule...</div>
      ) : (
        <div className="bg-surface-raised rounded-xl border border-edge-muted overflow-hidden shadow-sm">
          {/* Day headers */}
          <div className="grid grid-cols-7 bg-surface-sunken">
            {DAY_NAMES_SHORT.map((name) => (
              <div
                key={name}
                className="text-center text-[11px] font-semibold text-content-tertiary py-2 uppercase tracking-wider"
              >
                {name}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {cells.map((day, idx) => {
              if (day === null) {
                return (
                  <div
                    key={`empty-${idx}`}
                    className={`${cellHeight} border-b border-r border-edge-muted/30 bg-surface-sunken/30`}
                  />
                );
              }

              const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const todayHighlight = isToday(dateStr);
              const isSunday = new Date(dateStr + 'T12:00:00Z').getUTCDay() === 0;
              const isLastCol = (idx + 1) % 7 === 0;
              const isLastRow = idx >= cells.length - 7;

              // Client-side computation is the authoritative source for battalion
              // assignments — guarantees correctness even when DB data is stale.
              const computed = computeBattalionForDate(dateStr);
              const battalionNumber = computed.battalion;
              const isSundayContinuation = computed.isSunday;

              // API data is used only for supplementary metadata (holiday, override, notes)
              let entry = dayMap.get(dateStr) ?? null;
              if (isSunday && !entry) {
                entry = getSundayBattalionEntry(year, month, day, dayMap);
              }
              const battalionId = entry ? entry.battalion.id : battalionNumber;

              const borderClasses = `${isLastRow ? '' : 'border-b'} ${isLastCol ? '' : 'border-r'} border-edge-muted/30`;

              // If we still have no battalion (shouldn't happen), render bare cell
              if (!battalionNumber) {
                return (
                  <div
                    key={dateStr}
                    data-today={todayHighlight ? 'true' : undefined}
                    className={`${cellHeight} p-1.5 ${borderClasses} relative`}
                    style={{
                      backgroundColor: todayHighlight ? 'rgba(0, 103, 79, 0.08)' : 'transparent',
                    }}
                  >
                    {todayHighlight ? (
                      <div className="w-6 h-6 rounded-full bg-action-primary flex items-center justify-center">
                        <span className="text-[11px] font-bold text-content-on-primary">{day}</span>
                      </div>
                    ) : (
                      <span className="text-xs text-content-tertiary">{day}</span>
                    )}
                  </div>
                );
              }

              const bnStyle = getBattalionStyle(battalionNumber);
              const isOwn = memberBattalionId !== null && battalionId === memberBattalionId;
              const isHoliday = entry?.is_holiday && !isSundayContinuation;

              return (
                <div
                  key={dateStr}
                  data-today={todayHighlight ? 'true' : undefined}
                  className={`${cellHeight} p-1.5 ${borderClasses} relative transition-colors duration-100`}
                  style={{
                    backgroundColor: todayHighlight
                      ? 'rgba(0, 103, 79, 0.10)'
                      : isHoliday
                        ? 'rgba(255, 255, 255, 0.06)'
                        : 'transparent',
                    borderLeft: isOwn ? `2.5px solid ${bnStyle.text}` : undefined,
                  }}
                >
                  {/* Top row: date on left, battalion pill on right */}
                  <div className="flex items-start justify-between gap-0.5">
                    {/* Today circle or date number */}
                    {todayHighlight ? (
                      <div className="w-6 h-6 rounded-full bg-action-primary flex items-center justify-center z-10 shrink-0">
                        <span className="text-[11px] font-bold text-content-on-primary">{day}</span>
                      </div>
                    ) : (
                      <span
                        className="text-xs font-medium leading-6"
                        style={{
                          color: isHoliday ? bnStyle.text : undefined,
                        }}
                      >
                        <span className={isHoliday ? '' : 'text-content-primary'}>{day}</span>
                      </span>
                    )}

                    {/* Battalion pill — top right */}
                    <span
                      className="px-1.5 py-0.5 rounded text-[9px] font-bold leading-none whitespace-nowrap shrink-0"
                      style={{
                        backgroundColor: bnStyle.pillBg,
                        color: bnStyle.pillText,
                        border: `1px solid ${bnStyle.pillBorder}`,
                        opacity: isSundayContinuation ? 0.8 : 1,
                      }}
                    >
                      {isDesktop ? `Battalion ${battalionNumber}` : `B${battalionNumber}`}
                    </span>
                  </div>

                  {/* Holiday indicator */}
                  {isHoliday && (
                    <div className="text-[9px] font-semibold mt-0.5 truncate" style={{ color: bnStyle.text }}>
                      Holiday
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
